import { cloneElement, h } from 'preact';
import { useContext } from 'preact/hooks';
import { VOID_FN, deepMerge } from '../../../../../utils';
import { IconContext } from '../../../../common/components/svg/icons/icon-context';
import { useDialogContext } from '../../../../common/context/DialogContext';
import { useStyles } from '../../../../common/hooks';
import { TEXT_LEVEL_3_FONT_SIZE } from '../../../../common/styles';
import { TEXT_LEVEL_2_FONT_SIZE } from '../../../styles';
import { LABELS_FILL_COLOR, SECONDARY_FILL_COLOR, SELECTED_RADIO_BUTTON_COLOR, THIRD_TEXT_COLOR } from '../../../theme/colors';
import { SETTINGS_ICON_SIZE } from '../../controls/extra/styles';
import { ACTIVE_BULLET_STYLE, ACTIVE_RADIO_BUTTON_STYLE, RADIO_BUTTON_STYLE, ROW_ITEM_STYLE, SELECTABLE_DESCRIPTION_STYLES } from './styles';

const resolveSelectable = ({ radioButton, active }) => {
  const setStyles = useStyles();
  if (radioButton) {
    return (
      <div style={{ ...setStyles(RADIO_BUTTON_STYLE), borderColor: active ? THIRD_TEXT_COLOR : '#B4B4B4' }}>
        {active && <div style={ACTIVE_RADIO_BUTTON_STYLE} />}
      </div>
    );
  }

  return <div style={{ ...setStyles(ACTIVE_BULLET_STYLE), opacity: Number(active) }} />;
};

function Selectable({
  active,
  radioButton,
  label,
  ariaLabel,
  description,
  first,
  last,
  icon,
  onSelect = VOID_FN,
  closeOnActiveSelect = false,
  index,
  focusable = false,
  totalCount
}) {
  const Icon = useContext(IconContext);

  const dialogCtx = useDialogContext();
  const setStyles = useStyles();

  const handleSelect = () => {
    if (closeOnActiveSelect && active) dialogCtx.closeAll();
    onSelect();
  };

  const selectable = resolveSelectable({ radioButton, active });
  const iconSize = setStyles(SETTINGS_ICON_SIZE);

  return (
    <div
      style={{
        ...ROW_ITEM_STYLE,
        ...setStyles({
          default: { marginLeft: 0, minWidth: '100%' }
        }),
        ...setStyles(TEXT_LEVEL_3_FONT_SIZE),
        ...(last ? { marginBottom: 0 } : {})
      }}
    >
      <div
        className={`ftv-magneto--selectable ${focusable ? 'ftv-magneto--focusable-item' : ''}`}
        onClick={handleSelect}
        role="radio"
        aria-checked={active}
        aria-label={`${ariaLabel || label}, ${description ? `${description}, ` : ''}${index} sur ${totalCount}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.keyCode === 32) handleSelect(e);
          if (e.keyCode === 32) e.stopPropagation();
        }}
        tabIndex={0}
        style={setStyles({
          default: {
            cursor: 'pointer',
            lineHeight: '1.8',
            marginLeft: radioButton ? 3 : 14,
            width: '100%',
            ...(radioButton ? { color: 'PRIMARY_TEXT_COLOR' } : {}),
            ...(active && !radioButton ? { color: 'white' } : {}),
            paddingRight: 24,
            marginTop: first ? 0 : 14
          },
          small: { marginTop: first ? 0 : 14 },
          extraSmall: { paddingRight: 20, marginTop: first ? 0 : 14 }
        })}
      >
        <div style={{
          display: 'flex', alignItems: 'center', color: LABELS_FILL_COLOR

        }}
        >
          <div style={{ paddingBottom: radioButton ? 2 : 0 }}>
            {cloneElement(selectable, {
              ...selectable.props,
              style: setStyles({
                default: {
                  ...selectable.props.style, transform: `translate(${radioButton ? 0 : -14}px, 0px)`
                },
                'small+large': { transform: `translate(${radioButton ? 0 : -14}px, 0px)` }
              })
            })}
          </div>
          <span
            style={{
              ...setStyles({
                default: {
                  lineHeight: '1.8',
                  marginLeft: `${radioButton ? 10 : -6}px`,
                  fontFamily: 'Brown',
                  fontWeight: `${active && !radioButton ? 'bold' : 'normal'}`
                },
                small: {
                  maxWidth: 'unset'
                }
              }),
              ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
              ...(active ? { fontWeight: 700, color: SELECTED_RADIO_BUTTON_COLOR } : {})
            }}
          >
            {label}
          </span>

          {icon && (
            <div style={{
              ...setStyles({
                default: {
                  display: 'flex',
                  marginLeft: '8px'
                }
              })
            }}
            >
              <Icon
                tabIndex={-1}
                name={icon}
                color={SECONDARY_FILL_COLOR}
                width={iconSize.size}
                height={iconSize.size}
              />
            </div>
          )}
        </div>
        {description && (
          <div name="selectable-item-description" style={setStyles(deepMerge(SELECTABLE_DESCRIPTION_STYLES, TEXT_LEVEL_3_FONT_SIZE))}>
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

export default Selectable;
